import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonInfiniteScroll, IonInfiniteScrollContent, IonPage, IonTitle, IonToolbar, useIonModal } from '@ionic/react';
import { IonProgressBar } from '@ionic/react';
import { statsChartOutline } from 'ionicons/icons';
import { useCallback, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import DeviceEventItem from '../components/DeviceEventItem';
import { useEventsQuery } from '../query/eventsQuery';
import { LoadFailureStateContainer, LoadingStateContainer, NotLoggedInStateContainer } from '../components/StateContainer';
import ActivityFilter from '../components/ActivityFilter';
import { useTranslation } from 'react-i18next';
import { DeviceEventCriteria } from '../models/DeviceEventCriteria';
import RateExperienceBanner from '../components/RateExperienceBanner';
import RequiredPaymentBanner from '../components/RequiredPaymentBanner';
import ActivityOverview from '../components/ActivityOverview';
import HouseholdStatsModal from '../modals/HouseholdStatsModal';
import { IonContentVirtuosoBottomInset, IonContentVirtuosoTopInset, ionContentVirtuosoStyle } from '../components/IonContentVirtuoso';

interface ActivityListContext {
    criteria: DeviceEventCriteria;
    onCriteriaChange: (criteria: DeviceEventCriteria) => void;
    onShowRfidEvents: (rfidCode: string) => void;
}

const ActivityHeader: React.FC<{ context: ActivityListContext }> = ({ context }) => (
    <>
        <IonContentVirtuosoTopInset />
        <div className="oc-page-container">
            <RateExperienceBanner />
            <ActivityOverview onShowRfidEvents={context.onShowRfidEvents} />
            <ActivityFilter criteria={context.criteria} onCriteriaChange={context.onCriteriaChange} />
            <RequiredPaymentBanner />
        </div>
    </>
);

const Activity: React.FC = () => {
    const { t } = useTranslation('pages');
    const [criteria, setCriteria] = useState<DeviceEventCriteria>({});
    const eventsQuery = useEventsQuery(criteria);

    const [presentStatsModal, dismissStatsModal] = useIonModal(HouseholdStatsModal, {
        dismiss: (data: string, role: string) => dismissStatsModal(data, role)
    });

    const handleShowRfidEvents = useCallback((rfidCode: string) => {
        setCriteria((currentCriteria) => ({
            ...currentCriteria,
            rfidCode
        }));
    }, []);

    const activityListContext = useMemo<ActivityListContext>(() => ({
        criteria,
        onCriteriaChange: setCriteria,
        onShowRfidEvents: handleShowRfidEvents
    }), [criteria, handleShowRfidEvents]);

    const Footer = useCallback(() => {
        return (
            <>
                <IonInfiniteScroll onIonInfinite={async (event) => {
                    await eventsQuery.fetchNextPage();
                    await event.target.complete();
                }} disabled={!eventsQuery.hasNextPage} threshold="100px">
                    <IonInfiniteScrollContent />
                </IonInfiniteScroll>
                <IonContentVirtuosoBottomInset />
            </>
        )
    }, [eventsQuery]);

    const events = useMemo(() => {
        return eventsQuery.data?.pages.reduce((acc, page) => acc.concat(page), []) ?? [];
    }, [eventsQuery.data]);

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle>{t('activity.title')}</IonTitle>
                    <IonButtons slot="primary">
                        <IonButton onClick={() => presentStatsModal({ presentingElement: document.getElementsByTagName('ion-router-outlet')[0], cssClass: 'oc-dialog-m' })}>
                            <IonIcon slot="icon-only" icon={statsChartOutline} aria-label={t('activity.stats')} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                {eventsQuery.isFetching && !eventsQuery.isFetchingNextPage && <IonProgressBar type="indeterminate" />}
            </IonHeader>
            <IonContent fullscreen scrollY={false}>
                {eventsQuery.isLoading && <LoadingStateContainer />}
                {eventsQuery.isError && ({
                    401: <NotLoggedInStateContainer subtitle={t('activity.not_logged_in_subtitle')} />
                }[(eventsQuery.error as any).code as number] ?? <LoadFailureStateContainer subtitle={t('activity.couldnt_load_activity_subtitle')} error={eventsQuery.error} onRetry={() => eventsQuery.refetch()} />)}
                {eventsQuery.isSuccess &&
                    <>
                        <Virtuoso
                            className="ion-content-scroll-host"
                            style={ionContentVirtuosoStyle}
                            data={events}
                            context={activityListContext}
                            itemContent={(index, event) => <div className="oc-page-container"><DeviceEventItem deviceId={event.deviceId} eventId={event.eventId} accessToken={event.accessToken} /></div>}
                            itemSize={(el, field) => 128}
                            computeItemKey={(index, event) => `${event.deviceId}-${event.eventId}`}
                            components={{
                                Header: ActivityHeader,
                                Footer
                            }}
                            increaseViewportBy={{ top: 5, bottom: 5 }}
                        />
                    </>}
            </IonContent>
        </IonPage >
    );
};

export default Activity;
